import React, { Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { urlConfig } from '../config/urlConfig.js';
import Landinghero from '../components/Landinghero';
import TestimonialCarousel from '../components/TestimonialCarousel';
import { useNavigate } from 'react-router-dom';
import { FiFeather, FiTrendingUp, FiGlobe, FiMessageCircle, FiBookOpen, FiCompass, FiHeart, FiUsers, FiMic, FiShoppingBag, FiStar, FiLayers } from 'react-icons/fi';
import { trackCTAClick } from '../utils/analytics';

const archetypes = [
  { name: 'The Reflector',  subtitle: 'A mirror for clarity and self-understanding' },
  { name: 'The Healer',     subtitle: 'A gentle presence for emotional balance and recovery' },
  { name: 'The Visionary',  subtitle: 'A catalyst for possibility and forward momentum' },
  { name: 'The Guide',      subtitle: 'A steady compass in moments of choice' },
  { name: 'The Alchemist',  subtitle: 'A partner in transformation and reinvention' },
  { name: 'The Liberator',  subtitle: 'A voice for truth and authentic self-expression' },
];

// Lazy load heavy 3D component
const CelestialScene = lazy(() => import('../components/Celestialscene'));

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
  {/* Primary SEO */}
  <title>Andlegur | Digital Space for Reflection & Growth</title>
  <meta
    name="description"
    content="Andlegur is a journaling app and personal growth platform offering guided practices, reflection tools, and gentle support for clarity, balance, and growth."
  />
  <meta
    name="keywords"
    content="Andlegur, digital sanctuary, emotional well-being, personal growth platform, guided reflection, journaling app, self-discovery tools, mindfulness practices, emotional support, global community, inner clarity"
  />

  {/* Open Graph / Facebook */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content={urlConfig.app.pages.home} />
  <meta
    property="og:title"
    content="Andlegur | Your Digital Sanctuary for Reflection & Growth"
  />
  <meta
    property="og:description"
    content="A calm, supportive digital space designed to help you reflect, gain clarity, and grow—at your own pace."
  />
  <meta property="og:image" content={urlConfig.assets.ogImage} />
  <meta property="og:image:secure_url" content={urlConfig.assets.ogImage} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:site_name" content="Andlegur" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:url" content="https://andlegur.com/" />
  <meta
    name="twitter:title"
    content="Andlegur | Your Digital Sanctuary"
  />
  <meta
    name="twitter:description"
    content="Reflect, grow, and reconnect with what matters most in a calm, supportive digital sanctuary."
  />
  <meta name="twitter:image" content={urlConfig.assets.ogImage} />

  {/* Technical SEO */}
  <meta name="robots" content="index, follow" />
  <meta name="language" content="en" />
  <meta name="author" content="Andlegur" />
  <link rel="canonical" href="https://andlegur.com/" />

  {/* Structured Data */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Andlegur",
      "url": urlConfig.app.currentBaseURL,
      "logo": urlConfig.assets.logo,
      "description":
        "Andlegur is a digital sanctuary designed to support reflection, emotional clarity, personal growth, and meaningful connection.",
      "sameAs": [
        urlConfig.external.social.twitter,
        urlConfig.external.social.instagram,
        urlConfig.external.social.youtube,
        urlConfig.external.social.facebook
      ]
    })}
  </script>
</Helmet>

      
      <main className="font-serif text-indigo-100 overflow-x-hidden">
      <section>
        <Landinghero />
      </section>


        {/* ② This Is Your Space — image left, cards right */}
        <section className="relative overflow-hidden">
          <div className="grid sm:grid-cols-2">

            {/* Left — full-height image */}
            <div className="relative min-h-[50vh] sm:min-h-screen">
              <img
                src="/web_images/Andlegur%20web%201.webp"
                alt="Andlegur peaceful digital sanctuary space for reflection and personal growth"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                width="800"
                height="1000"
              />
              <div className="absolute inset-0 bg-linear-to-r from-transparent via-transparent to-[#05091B]" />
            </div>

            {/* Right — header + 3 cards */}
            <div className="flex flex-col justify-center px-8 sm:px-12 py-16 bg-[#05091B]">
              <p className="text-indigo-300 text-xs uppercase tracking-widest mb-4">You Found It</p>
              <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-snug">
                Overthinking keeping you awake? Find clarity
              </h2>
              <p className="text-indigo-200 text-base sm:text-lg leading-relaxed mb-10">
              Feeling stuck without knowing why. Andlegur helps you track your thoughts, understand your emotions, and take confident steps forward.
              </p>
              <div className="flex flex-col gap-5">
                {[
                  {
                    Icon: FiFeather,
                    title: 'Reflect',
                    desc: 'Stop circling the same thought. Write it down and watch the fog clear — in minutes, not months.',
                    accent: 'border-indigo-400',
                  },
                  {
                    Icon: FiTrendingUp,
                    title: 'Grow',
                    desc: 'Not generic advice you ignore. Growth tools built around how you specifically think, feel, and change.',
                    accent: 'border-violet-400',
                  },
                  {
                    Icon: FiGlobe,
                    title: 'Connect',
                    desc: 'A community that actually hears you — not to perform for, but to grow with. No comparison. No pressure.',
                    accent: 'border-sky-400',
                  },
                ].map(({ Icon, title, desc, accent }) => (
                  <div
                    key={title}
                    className={`flex items-start gap-5 bg-indigo-900/30 border-l-4 ${accent} rounded-xl px-6 py-5 hover:bg-indigo-900/50 transition-all duration-300`}
                  >
                    <div className="shrink-0 mt-0.5 p-2 rounded-lg bg-indigo-800/60">
                      <Icon className="w-6 h-6 text-indigo-300" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
                      <p className="text-indigo-200 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <button
                  onClick={() => { trackCTAClick('clear_your_mind_now', '/'); navigate('/register'); }}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Clear Your Mind Now →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ③ This Is For You If… */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2011.jpg"
              alt=""
              className="w-full h-full object-cover opacity-10 mix-blend-screen"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/80 to-[#05091B]" />
          </div>
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">You Will Recognize This</p>
              <h2 className="text-3xl sm:text-5xl font-light text-white leading-snug">
                Andlegur is for you if…
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { img: '/web_images/Andlegur%20web%2015.webp', text: 'You overthink situations and cannot seem to quiet your mind' },
                { img: '/web_images/Andlegur%20web%2018.webp', text: 'You feel emotions strongly but struggle to understand them' },
                { img: '/web_images/Andlegur%20web%2036.webp', text: 'You are going through a life change and need a space to process it' },
                { img: '/web_images/Andlegur%20web%2026.webp', text: 'You want clarity about your goals but do not know where to start' },
                { img: '/web_images/Andlegur%20web%2028.webp', text: 'You carry things you cannot say out loud to anyone in your life' },
                { img: '/web_images/Andlegur%20web%2025.webp', text: 'You want to grow — but you need structure, not another to-do list' },
              ].map(({ img, text }) => (
                <div key={text} className="relative rounded-2xl overflow-hidden group" style={{ minHeight: 200 }}>
                  <img
                    src={img}
                    alt={text}
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/50 to-transparent" />
                  <div className="relative z-10 flex items-end h-full p-6" style={{ minHeight: 200 }}>
                    <p className="text-white text-base font-light leading-snug">
                      <span className="text-indigo-400 mr-2">→</span>{text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl"
                onClick={() => navigate('/join')}
              >
                Discover Your Clarity →
              </button>
            </div>
          </div>
        </section>

        {/* ③b Before → After transformation */}
        <section className="relative py-20 border-t border-indigo-800/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">The Shift That Happens</p>
              <h2 className="text-3xl sm:text-5xl font-light text-white leading-snug">
                Two weeks of reflection.<br />
                <span className="text-indigo-300">A completely different relationship with yourself.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Before */}
              <div className="relative bg-[#0E1833] border border-rose-500/30 rounded-3xl overflow-hidden">
                <div className="px-8 pt-8 pb-4">
                  <div className="inline-flex items-center gap-2 bg-rose-600/20 border border-rose-500/30 rounded-full px-3 py-1 mb-6">
                    <span className="w-2 h-2 rounded-full bg-rose-400" />
                    <span className="text-rose-300 text-xs sm:text-sm uppercase tracking-widest font-semibold">Before Andlegur</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {[
                      'Thoughts spiral and never feel resolved',
                      'Emotions feel confusing and overwhelming',
                      'Life decisions feel heavy and unclear',
                      'You carry everything alone, in silence',
                      'Growth feels like something that happens to other people',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-rose-200 text-sm sm:text-base">
                        <span className="text-rose-400 mt-0.5 shrink-0 text-base leading-none">✕</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="/web_images/Andlegur%20web%2015.webp"
                    alt="Before Andlegur — overwhelmed thoughts and emotional confusion"
                    className="w-full h-full object-cover opacity-40"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-[#0E1833] via-transparent to-transparent" />
                </div>
              </div>

              {/* After */}
              <div className="relative bg-[#0E1833] border border-indigo-500/40 rounded-3xl overflow-hidden">
                <div className="px-8 pt-8 pb-4">
                  <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 rounded-full px-3 py-1 mb-6">
                    <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                    <span className="text-indigo-300 text-xs sm:text-sm uppercase tracking-widest font-semibold">After Andlegur</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {[
                      'Thoughts become clear the moment you write them',
                      'Emotions have names — and names give you power over them',
                      'Decisions feel grounded in what you actually value',
                      'You have a private space to process anything, anytime',
                      'Growth becomes a daily practice, not a distant dream',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-indigo-100 text-sm sm:text-base">
                        <span className="text-indigo-400 mt-0.5 shrink-0 text-base leading-none">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="/web_images/Andlegur%20web%2016.webp"
                    alt="After Andlegur — clarity, emotional understanding and personal growth"
                    className="w-full h-full object-cover opacity-40"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-[#0E1833] via-transparent to-transparent" />
                </div>
              </div>
            </div>
            <div className="text-center mt-12">
              <button
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl"
                onClick={() => navigate('/join')}
              >
                Create Your Private Space →
              </button>
            </div>
          </div>
        </section>


        {/* Single CelestialScene wrapper for sections ④ and ⑦ */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 w-full h-full z-0 pointer-events-none overflow-hidden">
            <Suspense fallback={null}>
              <CelestialScene />
            </Suspense>
          </div>

        {/* ④ The Feeling of Progress — alternating image + text timeline */}
        <section className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <p className="text-indigo-400 text-sm uppercase tracking-widest mb-3">The Transformation Arc</p>
              <h2 className="text-3xl sm:text-5xl font-light text-white">The Feeling of Progress</h2>
            </div>
            <div className="relative">
              <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-px bg-indigo-600/30 -translate-x-1/2" />
              {[
                { day: 'Day 1',       heading: 'You arrive.',  text: "You find a space that doesn't demand anything from you. No performance, no agenda, no judgment. Just a gentle invitation to begin.", img: '/web_images/Andlegur%20web%206.webp', alt: 'Andlegur welcoming a first-time user into a calm digital sanctuary for reflection', flip: false },
                { day: 'Week 1',      heading: 'You reflect.', text: 'Guided journaling and gentle prompts help you see yourself more clearly. Emotions find language. Patterns emerge. Understanding begins.', img: '/web_images/Andlegur%20web%204.webp', alt: 'Andlegur journaling and guided reflection tools helping users gain emotional clarity', flip: true },
                { day: 'Month 1',     heading: 'You shift.',   text: 'With daily practices and your archetype companion, something changes — not forced, not rushed. Naturally, from the inside out.', img: '/web_images/Andlegur%20web%205.webp', alt: 'Andlegur user experiencing personal growth shift through archetype guidance and daily rituals', flip: false },
                { day: 'Your Journey',heading: 'You become.',  text: "Look back and see how far you've come. Look forward and know — you're not walking alone. You never were.", img: '/web_images/Andlegur%20web%203.webp', alt: 'Andlegur long-term personal transformation — becoming the best version of yourself', flip: true },
              ].map((m, i) => (
                <div key={i} className={`relative flex flex-col ${m.flip ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 mb-20`}>
                  <div className="w-full sm:w-5/12">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                      <img src={m.img} alt={m.alt} className="w-full h-full object-cover" loading="lazy" width="600" height="400" />
                      <div className="absolute inset-0 bg-linear-to-t from-indigo-950/60 via-transparent to-transparent" />
                    </div>
                  </div>
                  <div className="hidden sm:flex w-2/12 justify-center items-center">
                    <div className="w-5 h-5 rounded-full bg-indigo-500 border-2 border-indigo-300 shadow-lg shadow-indigo-500/60 z-10" />
                  </div>
                  <div className="w-full sm:w-5/12">
                    <p className="text-indigo-400 text-xs uppercase tracking-widest mb-2">{m.day}</p>
                    <h3 className="text-3xl sm:text-4xl font-light text-white mb-4">{m.heading}</h3>
                    <p className="text-indigo-200 leading-relaxed">{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-2">
              <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg text-lg" onClick={() => navigate('/register')}>
                Begin Your Practice →
              </button>
            </div>
          </div>
        </section>

        {/* ⑤ Testimonials */}
        <section className="relative z-10">
          <TestimonialCarousel />
        </section>

        {/* ⑥ Your Archetype — image left + selector list right */}
        <section className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-12 items-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="/web_images/Andlegur%20web%207.webp" alt="Andlegur archetype companion guide — choose your personal growth path" className="w-full object-cover" style={{ maxHeight: '580px' }} loading="lazy" width="600" height="580" />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/85 via-indigo-950/10 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="text-white text-2xl font-light leading-snug">Your companion<br />is already waiting.</p>
                </div>
              </div>
              <div>
                <p className="text-indigo-300 text-sm sm:text-base uppercase tracking-widest mb-3">6 Archetypes</p>
                <h2 className="text-3xl sm:text-4xl font-light text-white mb-4 leading-snug">Which voice<br />feels like home?</h2>
                <p className="text-indigo-100 mb-8 leading-relaxed text-base">Each archetype listens, guides, and supports with a different kind of heart. Your instinct already knows — trust it.</p>
                <div className="space-y-3">
                  {archetypes.map((arch) => (
                    <button key={arch.name} onClick={() => navigate('/sanctuary/archetypes')} className="w-full text-left flex items-center justify-between px-5 py-4 rounded-xl bg-indigo-900/40 border border-indigo-600/30 hover:bg-indigo-700/50 hover:border-indigo-400/60 transition-all duration-200 group">
                      <div>
                        <p className="text-white font-medium group-hover:text-indigo-200 transition-colors">{arch.name}</p>
                        <p className="text-indigo-200 text-sm mt-0.5">{arch.subtitle}</p>
                      </div>
                      <svg className="w-5 h-5 text-indigo-500 group-hover:text-indigo-300 group-hover:translate-x-1 transition-all shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
                <button className="mt-8 w-full bg-indigo-600 hover:bg-indigo-500 text-white py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-xl" onClick={() => navigate('/sanctuary/archetypes')}>
                  Discover Your Archetype →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ⑦ What Lives Inside — 3-image mosaic + TableImpact */}
        <section className="relative py-16 overflow-hidden">
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 mb-10">
            <div className="text-center mb-10">
              <p className="text-indigo-300 text-sm sm:text-base uppercase tracking-widest mb-3">Inside the Sanctuary</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white">What Lives Inside Andlegur</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 rounded-2xl overflow-hidden shadow-2xl h-64 sm:h-80">
              <img src="/web_images/Andlegur%20web%208.webp" alt="Andlegur app mindfulness and reflection features inside the digital sanctuary" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
              <img src="/web_images/Andlegur%20web%2033.webp" alt="Andlegur members journaling and practicing tools for personal growth" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
              <img src="/web_images/Andlegur%20web%209.webp" alt="Andlegur platform community and personal growth experience screen" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
            </div>
          </div>
          {/* Inline feature grid — replaces TableImpact */}
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-12">
            <div className="text-center mb-12">
              <p className="text-indigo-300 text- uppercase tracking-widest mb-3">Everything in One Place</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white">Why Andlegur Is Different</h2>
              <p className="text-indigo-200 text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                Andlegur combines AI insight, reflective practices, community, and supportive tools — without pressure or rigid systems.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  Icon: FiMessageCircle,
                  color: 'from-indigo-600/30 to-indigo-900/10',
                  border: 'border-indigo-500/40',
                  icon_color: 'text-indigo-300',
                  title: 'AI Companions',
                  desc: 'Personalized AI shaped by your reflection and emotional context — feel heard, supported, and understood.',
                },
                {
                  Icon: FiCompass,
                  color: 'from-violet-600/30 to-violet-900/10',
                  border: 'border-violet-500/40',
                  icon_color: 'text-violet-300',
                  title: 'Guided Practices',
                  desc: 'Timed prompts and practices for daily reflection — build calm, balance, and emotional consistency.',
                },
                {
                  Icon: FiBookOpen,
                  color: 'from-sky-600/30 to-sky-900/10',
                  border: 'border-sky-500/40',
                  icon_color: 'text-sky-300',
                  title: 'Journaling & Insights',
                  desc: 'Guided journaling with pattern recognition — process emotions and track growth over time.',
                },
                {
                  Icon: FiHeart,
                  color: 'from-rose-600/30 to-rose-900/10',
                  border: 'border-rose-500/40',
                  icon_color: 'text-rose-300',
                  title: 'Emotional Intelligence',
                  desc: 'Adaptive AI that responds to your mood and engagement — receive support that evolves with you.',
                },
                {
                  Icon: FiUsers,
                  color: 'from-teal-600/30 to-teal-900/10',
                  border: 'border-teal-500/40',
                  icon_color: 'text-teal-300',
                  title: 'Community Space',
                  desc: 'A shared space for reflection and mutual support — feel connected without pressure or comparison.',
                },
                {
                  Icon: FiMic,
                  color: 'from-amber-600/30 to-amber-900/10',
                  border: 'border-amber-500/40',
                  icon_color: 'text-amber-300',
                  title: 'Voice-Guided Rituals',
                  desc: 'Spoken meditations and affirmations — feel soothed, inspired, and emotionally regulated.',
                },
                {
                  Icon: FiShoppingBag,
                  color: 'from-purple-600/30 to-purple-900/10',
                  border: 'border-purple-500/40',
                  icon_color: 'text-purple-300',
                  title: 'Cosmic Shop',
                  desc: 'Digital and physical tools to create intentional moments for grounding, creativity, and reset.',
                },
                {
                  Icon: FiLayers,
                  color: 'from-cyan-600/30 to-cyan-900/10',
                  border: 'border-cyan-500/40',
                  icon_color: 'text-cyan-300',
                  title: 'Membership Tiers',
                  desc: 'Flexible access to deeper features — commit to growth with structure, not pressure.',
                },
              ].map(({ Icon, color, border, icon_color, title, desc }) => (
                <div
                  key={title}
                  className={`relative bg-linear-to-br ${color} border ${border} rounded-2xl p-6 flex flex-col gap-4 hover:scale-[1.02] transition-all duration-300 shadow-lg group`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border ${border}`}>
                    <Icon className={`w-5 h-5 ${icon_color}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">{title}</h3>
                    <p className="text-indigo-200 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        </div>
        {/* end single CelestialScene wrapper */}

        {/* ⑧ The Community — stats left + image right */}
        <section className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-indigo-300 text-sm sm:text-base uppercase tracking-widest mb-3">A Global Impact</p>
                <h2 className="text-3xl sm:text-5xl font-light text-white mb-6 leading-snug">Together,<br />Across the World</h2>
                <p className="text-indigo-200 text-sm sm:text-lg leading-relaxed mb-10">Andlegur supports people at every stage of life — through reflection, change, and growth. Whether you're exploring self-discovery or seeking career clarity, you are not walking alone.</p>
                <div className="grid grid-cols-2 gap-4 mb-10">
                  {[
                    { value: 'Judgment-Free', label: 'No comparison, no pressure, no shame' },
                    { value: 'All Ages',       label: 'A sanctuary for every stage of life' },
                    { value: 'Always On',      label: 'Guidance accessible at any time' },
                    { value: '1 Intention',    label: 'Reflect. Grow. Align. Connect.' },
                  ].map((stat) => (
                    <div key={stat.value} className="bg-indigo-900/50 border border-indigo-600/30 rounded-xl p-5">
                      <p className="text-white font-bold">{stat.value}</p>
                      <p className="text-indigo-200 text-sm mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg" onClick={() => navigate('/community')}>
                  Join the Community →
                </button>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="/web_images/Andlegur%20web%2021.webp" alt="Andlegur global community members connecting through shared reflection and personal growth" className="w-full object-cover" style={{ maxHeight: '540px' }} loading="lazy" width="600" height="540" />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/70 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* ⑨ Final CTA — full immersive image backdrop */}
        <section className="relative min-h-140 flex items-center justify-center overflow-hidden">
          <img src="/web_images/Andlegur%20web%202.webp" alt="Andlegur invitation — begin your journey of reflection, growth, and self-discovery" className="absolute inset-0 w-full h-full object-cover" loading="lazy" width="1200" height="560" />
          <div className="absolute inset-0 bg-[#0E1833]/90" />
          <div className="relative z-10 text-center max-w-3xl mx-auto px-4 sm:px-6 py-20">
            <p className="text-indigo-300 text-lg uppercase tracking-widest mb-5">Your Moment Begins Here</p>
            <h2 className="text-4xl sm:text-6xl font-light text-white mb-6 leading-snug">Your mind is full.<br /><span className="text-indigo-300">This is where you clear it.</span></h2>
            <p className="text-indigo-200 text-lg mb-12 leading-relaxed">Write your first thought. Understand what it actually means. Start your reflection practice. Under 2 minutes to begin — free forever.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl" onClick={() => { trackCTAClick('clear_my_mind_footer', '/'); navigate('/sanctuary/membership'); }}>
                Clear My Mind Now →
              </button>
              <button className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300" onClick={() => { trackCTAClick('download_free_footer', '/'); navigate('/get-started'); }}>
                Download Free
              </button>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default HomePage;
